/**
 * usePackageData Hook
 *
 * RTKQ-based package data with selectFromResult for granular subscriptions.
 * Provides core package info without transmission or components.
 *
 * Features optimistic updates for smooth status transitions (e.g., generating).
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useMatter } from '@/hooks/useMatter';
import {
  correspondenceApi,
  type CorrespondencePackageSchema,
  type CorrespondencePackageType,
  type RecipientEntityType,
  type RenderStatus,
  type SendPackageResponse,
  type Transmission,
  useCorrespondencePackageCreateMutation,
  useCorrespondencePackageGetOrCreateForCoverageMutation,
  useCorrespondencePackageRenderMutation,
  useCorrespondencePackageRetrieveQuery,
  useCorrespondencePackageSendMutation,
  useCorrespondencePackageUpdateMutation,
} from '@/services/api/correspondenceService';

// ============================================================================
// Types
// ============================================================================

export interface UsePackageDataOptions {
  /** Existing package ID (if editing) */
  packageId?: string;
  /** Package type for new packages */
  packageType: CorrespondencePackageType;
  /** Initial recipient for new packages */
  recipientEntityType?: RecipientEntityType;
  recipientEntityId?: string;
  /** Coverage ID for insurance packages */
  coverageId?: string;
  /** Whether to include default templates */
  withDefaults?: boolean;
  /** Callback when a new package is created (for context sync) */
  onPackageCreated?: (packageId: string) => void;
}

export interface UsePackageDataResult {
  // Core data
  packageId: string | undefined;
  packageData: CorrespondencePackageSchema | undefined;
  status: RenderStatus | undefined;
  packageSource: 'generated' | 'manually_uploaded' | undefined;

  // Recipient info (from package)
  recipientEntityType: RecipientEntityType | null;
  recipientEntityId: string | null;
  recipientDisplayName: string | null;

  // Computed flags
  isTransmitted: boolean;
  canEdit: boolean;
  canSend: boolean;

  // URLs
  downloadUrl: string | null;
  renderedPdfUrl: string | null;

  // Actions
  refetch: () => void;
  setRecipient: (
    entityType: 'insurance_carrier' | 'medical_provider' | 'police_department',
    entityId: string,
    displayName?: string,
  ) => Promise<void>;
  clearRecipient: () => Promise<void>;
  render: () => Promise<void>;
  send: (
    transmission: Transmission,
  ) => Promise<SendPackageResponse | undefined>;

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isRendering: boolean;
  isSending: boolean;
  isUpdatingRecipient: boolean;
  createError: string | null;
}

// ============================================================================
// Hook
// ============================================================================

export function usePackageData({
  packageId,
  packageType,
  recipientEntityType: initialRecipientEntityType,
  recipientEntityId: initialRecipientEntityId,
  coverageId,
  withDefaults = false,
  onPackageCreated,
}: UsePackageDataOptions): UsePackageDataResult {
  const { matterId } = useMatter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // Track creation state (but not the ID - that's managed by context)
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ========== Package Creation ==========
  const [createPackage] = useCorrespondencePackageCreateMutation();
  const [getOrCreateForCoverage] =
    useCorrespondencePackageGetOrCreateForCoverageMutation();

  useEffect(() => {
    // Skip if we already have a packageId or are currently creating
    if (packageId || isCreating) return;

    const doCreate = async () => {
      setIsCreating(true);
      setCreateError(null);

      try {
        // Use simplified recipient_entity_type + recipient_entity_id pattern
        const recipientFields =
          initialRecipientEntityId && initialRecipientEntityType
            ? {
                recipient_entity_type: initialRecipientEntityType,
                recipient_entity_id: initialRecipientEntityId,
              }
            : {};

        const result = coverageId
          ? await getOrCreateForCoverage({
              matterId,
              createCorrespondencePackage: {
                package_type: packageType,
                with_defaults: withDefaults,
                ...recipientFields,
                coverage_id: coverageId,
              },
            }).unwrap()
          : await createPackage({
              matterId,
              createCorrespondencePackage: {
                package_type: packageType,
                with_defaults: withDefaults,
                ...recipientFields,
              },
            }).unwrap();

        // Notify context of the new packageId (single source of truth)
        onPackageCreated?.(result.id);
      } catch (err: unknown) {
        const errorMessage =
          err && typeof err === 'object' && 'data' in err
            ? String(
                (err as { data?: { error?: string } }).data?.error ||
                  'Failed to create package',
              )
            : 'Failed to create package';
        setCreateError(errorMessage);
      } finally {
        setIsCreating(false);
      }
    };

    doCreate();
  }, [
    packageId,
    isCreating,
    createPackage,
    getOrCreateForCoverage,
    matterId,
    packageType,
    initialRecipientEntityType,
    initialRecipientEntityId,
    coverageId,
    withDefaults,
    onPackageCreated,
  ]);

  // Track if we should poll faster (when generating)
  const [shouldFastPoll, setShouldFastPoll] = useState(false);

  // ========== RTKQ Query with selectFromResult ==========
  const {
    data: packageData,
    isLoading: isLoadingQuery,
    refetch,
  } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    {
      skip: !packageId,
      pollingInterval: shouldFastPoll ? 1000 : 3000,
      skipPollingIfUnfocused: true,
      selectFromResult: ({ data, isLoading }) => ({
        data,
        isLoading,
      }),
    },
  );

  // Update polling frequency based on package status
  // Poll fast when queued or generating to quickly pick up status changes
  useEffect(() => {
    const status = packageData?.status;
    setShouldFastPoll(
      status === 'queued_for_render' || status === 'generating',
    );
  }, [packageData?.status]);

  // ========== Mutations ==========
  const [updatePackage, { isLoading: isUpdatingRecipient }] =
    useCorrespondencePackageUpdateMutation();
  const [renderPackage, { isLoading: isRendering }] =
    useCorrespondencePackageRenderMutation();
  const [sendPackage, { isLoading: isSending }] =
    useCorrespondencePackageSendMutation();

  // ========== Derived Values ==========
  const isTransmitted = !!packageData?.transmitted_at;
  const canEdit = packageData?.is_mutable !== false && !isTransmitted;
  const canSend = packageData?.status === 'ready' && !isTransmitted;

  // Use simplified recipient fields from backend
  const recipientEntityType = packageData?.recipient_entity_type ?? null;
  const recipientEntityId = packageData?.recipient_id ?? null;

  // ========== Actions ==========
  const setRecipient = useCallback(
    async (
      entityType:
        | 'insurance_carrier'
        | 'medical_provider'
        | 'police_department',
      entityId: string,
      displayName?: string,
    ) => {
      if (!packageId) return;
      await updatePackage({
        matterId,
        id: packageId,
        patchedUpdateCorrespondencePackage: {
          recipient_entity_type: entityType,
          recipient_entity_id: entityId,
          recipient_display_name: displayName,
        },
      }).unwrap();
    },
    [updatePackage, matterId, packageId],
  );

  const clearRecipient = useCallback(async () => {
    if (!packageId) return;
    await updatePackage({
      matterId,
      id: packageId,
      patchedUpdateCorrespondencePackage: { clear_recipient: true },
    }).unwrap();
  }, [updatePackage, matterId, packageId]);

  const render = useCallback(async () => {
    if (!packageId) return;

    // Optimistic update: immediately set status to 'queued_for_render' for smooth UX
    // Backend will confirm this status synchronously, then transition to 'generating'
    const patchResult = dispatch(
      correspondenceApi.util.updateQueryData(
        'correspondencePackageRetrieve',
        { matterId, id: packageId },
        (draft) => {
          draft.status = 'queued_for_render';
          // Mark all components as queued for visual feedback
          if (draft.components) {
            draft.components.forEach((component) => {
              component.status = 'queued_for_render';
            });
          }
        },
      ),
    );

    // Start fast polling immediately
    setShouldFastPoll(true);

    try {
      await renderPackage({ matterId, id: packageId }).unwrap();
      // Backend will now return updated status, polling will pick it up
    } catch (error) {
      // Rollback on error
      patchResult.undo();
      setShouldFastPoll(false);
      throw error;
    }
  }, [renderPackage, matterId, packageId, dispatch]);

  const send = useCallback(
    async (
      transmission: Parameters<
        typeof sendPackage
      >[0]['sendPackageRequest']['transmission'],
    ) => {
      if (!packageId) return undefined;
      return sendPackage({
        matterId,
        id: packageId,
        sendPackageRequest: { package_id: packageId, transmission },
      }).unwrap();
    },
    [sendPackage, matterId, packageId],
  );

  return {
    // Core data
    packageId,
    packageData,
    status: packageData?.status,
    packageSource: packageData?.package_source,

    // Recipient
    recipientEntityType,
    recipientEntityId,
    recipientDisplayName: packageData?.recipient_display_name ?? null,

    // Flags
    isTransmitted,
    canEdit,
    canSend,

    // URLs
    downloadUrl: packageData?.download_url ?? null,
    renderedPdfUrl: packageData?.rendered_pdf_url ?? null,

    // Actions
    refetch,
    setRecipient,
    clearRecipient,
    render,
    send,

    // Loading
    isLoading: isCreating || isLoadingQuery,
    isCreating,
    isRendering,
    isSending,
    isUpdatingRecipient,
    createError,
  };
}
